<script setup>
/**
 * One campaign on the unified dashboard grid: a mini widget preview of the
 * message, the derived status, the channel it runs on, its targeting summary,
 * and the row of actions the account admin can take on it.
 *
 * Presentational only — every action is emitted for the page to dispatch.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageStamp } from 'shared/helpers/timeHelper';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';
import { INBOX_TYPES, getInboxIconByType } from 'dashboard/helper/inbox';
import { toneClassesFor, toneForInbox } from 'dashboard/helper/channelTone';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import StatusChip from 'dashboard/components-next/status-chip/StatusChip.vue';
import {
  CAMPAIGN_STATUS,
  STATUS_CHIP_SUPPORTED_STATUSES,
  deriveCampaignStatus,
} from 'dashboard/components-next/Campaigns/helpers/campaignStatus';
import {
  FREQUENCIES,
  VISITOR_TYPES,
} from 'dashboard/components-next/Campaigns/helpers/campaignBuilder';
import CampaignMessagePreview from './CampaignMessagePreview.vue';

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits([
  'toggleEnabled',
  'duplicate',
  'edit',
  'test',
  'sendTest',
  'delete',
]);

const { t } = useI18n();

const isOngoing = computed(
  () => props.campaign.campaign_type === CAMPAIGN_TYPES.ONGOING
);

const isLiveChat = computed(
  () =>
    isOngoing.value && props.campaign.inbox?.channel_type === INBOX_TYPES.WEB
);

const isWhatsApp = computed(
  () => props.campaign.inbox?.channel_type === INBOX_TYPES.WHATSAPP
);

const derivedStatus = computed(() =>
  deriveCampaignStatus({
    campaignStatus: props.campaign.campaign_status,
    campaignType: props.campaign.campaign_type,
    enabled: props.campaign.enabled,
    scheduledAt: props.campaign.scheduled_at,
  })
);

// `active` and `completed` have no member in StatusChip's status vocabulary,
// so they keep the same fallback pill CampaignCard renders.
const hasStatusChip = computed(() =>
  STATUS_CHIP_SUPPORTED_STATUSES.includes(derivedStatus.value)
);

const isActive = computed(() => derivedStatus.value === CAMPAIGN_STATUS.ACTIVE);

const statusPillClasses = computed(() =>
  isActive.value
    ? 'bg-n-teal-3 text-n-teal-11'
    : 'bg-n-violet-3 text-n-violet-11'
);

const campaignStatusLabel = computed(() =>
  isActive.value
    ? t('CAMPAIGNS.CARD.STATUS.ACTIVE')
    : t('CAMPAIGNS.CARD.STATUS.COMPLETED')
);

const inboxIcon = computed(() => {
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
  } = props.campaign.inbox ?? {};
  return getInboxIconByType(type, medium, 'fill', voiceEnabled);
});

const inboxBadgeClasses = computed(() =>
  toneClassesFor(toneForInbox(props.campaign.inbox))
);

const targetingChips = computed(() => {
  const chips = [];

  if (isOngoing.value) {
    const {
      url,
      time_on_page: timeOnPage,
      visitor_type: visitorType,
      frequency,
    } = props.campaign.trigger_rules ?? {};

    // The defaults ("everyone", "every visit") add nothing — only narrowed
    // targeting earns a chip.
    if (
      [VISITOR_TYPES.FIRST_TIME, VISITOR_TYPES.RETURNING].includes(visitorType)
    ) {
      chips.push({
        key: 'visitor_type',
        icon: 'i-lucide-users',
        label: t(
          `CAMPAIGN.ALL.CARD.TARGETING.VISITOR_TYPE.${visitorType.toUpperCase()}`
        ),
      });
    }
    if (frequency === FREQUENCIES.ONCE) {
      chips.push({
        key: 'frequency',
        icon: 'i-lucide-repeat-1',
        label: t('CAMPAIGN.ALL.CARD.TARGETING.FREQUENCY.ONCE'),
      });
    }
    if (url) {
      chips.push({
        key: 'url',
        icon: 'i-lucide-link',
        label: t('CAMPAIGN.ALL.CARD.TARGETING.URL', { url }),
      });
    }
    if (timeOnPage) {
      chips.push({
        key: 'time_on_page',
        icon: 'i-lucide-timer',
        label: t('CAMPAIGN.ALL.CARD.TARGETING.TIME_ON_PAGE', {
          time: timeOnPage,
        }),
      });
    }
  } else {
    const audienceCount = props.campaign.audience?.length ?? 0;
    if (audienceCount) {
      chips.push({
        key: 'audience',
        icon: 'i-lucide-users',
        label: t('CAMPAIGN.ALL.CARD.TARGETING.AUDIENCE', {
          count: audienceCount,
        }),
      });
    }
    if (props.campaign.scheduled_at) {
      chips.push({
        key: 'scheduled_at',
        icon: 'i-lucide-calendar-clock',
        label: t('CAMPAIGN.ALL.CARD.TARGETING.SCHEDULED', {
          date: messageStamp(props.campaign.scheduled_at, 'LLL d, h:mm a'),
        }),
      });
    }
  }

  return chips;
});

const isPaused = computed(() => derivedStatus.value === CAMPAIGN_STATUS.PAUSED);

// A draft has no run state to pause or resume — publishing (via the builder)
// is the only way forward, so the toggle stays hidden until then.
const isDraft = computed(() => derivedStatus.value === CAMPAIGN_STATUS.DRAFT);

const toggleLabel = computed(() =>
  isPaused.value
    ? t('CAMPAIGN.ALL.CARD.ACTIONS.RESUME')
    : t('CAMPAIGN.ALL.CARD.ACTIONS.PAUSE')
);

// Live chat is tested by opening the page it targets; the one-off channels are
// tested by actually sending the message to a single number.
const testLabel = computed(() =>
  isLiveChat.value
    ? t('CAMPAIGN.ALL.CARD.ACTIONS.TEST')
    : t('CAMPAIGN.ALL.CARD.ACTIONS.SEND_TEST')
);

// Live chat campaigns edit in the full-page builder, WhatsApp ones in their own
// dialog. A run that has already started or finished is out of reach either way:
// the backend refuses any update to a processing or completed campaign.
const isEditable = computed(
  () =>
    isLiveChat.value ||
    (isWhatsApp.value &&
      ![CAMPAIGN_STATUS.PROCESSING, CAMPAIGN_STATUS.COMPLETED].includes(
        derivedStatus.value
      ))
);

const handleTest = () => {
  if (isLiveChat.value) {
    emit('test');
    return;
  }
  emit('sendTest');
};
</script>

<template>
  <article
    class="flex flex-col overflow-hidden rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
  >
    <CampaignMessagePreview
      :title="campaign.title"
      :message="campaign.message"
      :theme-color="campaign.content?.theme_color"
    />

    <div class="flex flex-col flex-1 gap-3 px-5 py-4">
      <div class="flex flex-wrap items-center gap-2">
        <h3 class="mb-0 text-sm font-medium truncate text-n-slate-12">
          {{ campaign.title }}
        </h3>
        <StatusChip v-if="hasStatusChip" :status="derivedStatus" size="sm" />
        <span
          v-else
          class="inline-flex items-center h-5 px-2 font-medium rounded-full text-label-small"
          :class="statusPillClasses"
        >
          {{ campaignStatusLabel }}
        </span>
      </div>

      <div class="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <span
          class="inline-flex items-center gap-1 px-2 py-0.5 max-w-full font-medium rounded-full text-label-small"
          :class="inboxBadgeClasses"
        >
          <Icon :icon="inboxIcon" class="flex-shrink-0 size-3" />
          <span class="truncate">{{ campaign.inbox?.name }}</span>
        </span>
        <span
          v-for="chip in targetingChips"
          :key="chip.key"
          class="inline-flex items-center gap-1 px-2 py-0.5 max-w-full font-medium rounded-full text-label-small bg-n-alpha-2 text-n-slate-11"
        >
          <Icon :icon="chip.icon" class="flex-shrink-0 size-3" />
          <span class="truncate">{{ chip.label }}</span>
        </span>
      </div>

      <div class="flex items-center gap-2 pt-1 mt-auto">
        <Button
          v-if="isOngoing && !isDraft"
          v-tooltip.top="toggleLabel"
          variant="faded"
          size="sm"
          color="slate"
          :icon="isPaused ? 'i-lucide-play' : 'i-lucide-pause'"
          :aria-label="toggleLabel"
          @click="emit('toggleEnabled')"
        />
        <Button
          v-tooltip.top="t('CAMPAIGN.ALL.CARD.ACTIONS.DUPLICATE')"
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-copy"
          :aria-label="t('CAMPAIGN.ALL.CARD.ACTIONS.DUPLICATE')"
          @click="emit('duplicate')"
        />
        <Button
          v-if="isEditable"
          v-tooltip.top="t('CAMPAIGN.ALL.CARD.ACTIONS.EDIT')"
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-sliders-vertical"
          :aria-label="t('CAMPAIGN.ALL.CARD.ACTIONS.EDIT')"
          @click="emit('edit')"
        />
        <Button
          v-tooltip.top="testLabel"
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-flask-conical"
          :aria-label="testLabel"
          @click="handleTest"
        />
        <Button
          v-tooltip.top="t('CAMPAIGN.ALL.CARD.ACTIONS.DELETE')"
          variant="faded"
          color="ruby"
          size="sm"
          icon="i-lucide-trash"
          class="ltr:ml-auto rtl:mr-auto"
          :aria-label="t('CAMPAIGN.ALL.CARD.ACTIONS.DELETE')"
          @click="emit('delete')"
        />
      </div>
    </div>
  </article>
</template>
